import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../core/breadcrumbs";
import { Filter, Box, Zap } from "react-feather";
import Select from "react-select";
import { getInventoryReport } from "../../service/ApiServices";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const InventoryReport = () => {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    fetchInventoryReport();
    const uniqueCategories = [
      ...new Set(inventoryData.map((item) => item.category)),
    ];
    const uniqueBrands = [...new Set(inventoryData.map((item) => item.brand))];
    console.log(uniqueCategories);
    setCategories(uniqueCategories.map((cat) => ({ value: cat, label: cat })));
    setBrands(uniqueBrands.map((brand) => ({ value: brand, label: brand })));
  }, []);

  const fetchInventoryReport = async () => {
    try {
      setLoading(true);
      const data = await getInventoryReport(
        searchTerm,
        selectedCategory?.value,
        selectedBrand?.value
      );
      setInventoryData(data);
    } catch (error) {
      console.error("Error fetching inventory report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInventoryReport();
  };

  const handleFilterChange = () => {
    fetchInventoryReport();
  };

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      inventoryData.map((item) => ({
        "Product Name": item.productName,
        Category: item.category,
        "Sub Category": item.subcategories[0]?.name || "N/A",
        Brand: item.brand,
        Unit: item.unit,
        "Stock Quantity": item.stockQty,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory Report");
    XLSX.writeFile(workbook, "inventory_report.xlsx");
  };

  const exportToPDF = () => {
    try {
      const customWidth = 210;
      const customHeight = 297;

      const doc = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: [customWidth, customHeight],
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(20);
      doc.text("The Suit - Inventory Report", pageWidth / 2, 20, {
        align: "center",
      });

      doc.setDrawColor(150, 150, 181);
      doc.line(0, 30, pageWidth, 30);

      const inventoryRows = inventoryData.map((item) => [
        item.productName,
        item.category || "N/A",
        item.subcategories[0]?.name || "N/A",
        item.brand || "N/A",
        item.unit || "N/A",
        item.stockQty,
      ]);

      doc.autoTable({
        startY: 40,
        head: [
          [
            "Product Name",
            "Category",
            "Sub Category",
            "Brand",
            "Unit",
            "Stock Quantity",
          ],
        ],
        body: inventoryRows,
        theme: "grid",
        styles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
        headStyles: { fillColor: [74, 74, 73], textColor: [255, 255, 255] },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 30 },
          2: { cellWidth: 35 },
          3: { cellWidth: 25 },
          4: { cellWidth: 20 },
          5: { cellWidth: 30 },
        },
        margin: { bottom: 5 },
      });

      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const view = window.open();
      view.document.write(
        `<iframe width="100%" height="100%" src="${pdfUrl}"></iframe>`
      );
      view.document.close();
    } catch (error) {
      console.error("Error in exportToPDF function:", error);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Inventory Report"
          subtitle="Manage Your Inventory Report"
          downloadPDF={exportToPDF}
          downloadExcel={exportToExcel}
        />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search By Product Name"
                    className="form-control form-control-sm formsearch"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-searchset" onClick={handleSearch}>
                    <i data-feather="search" className="feather-search" />
                  </button>
                </div>
              </div>
              <div className="search-path">
                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-filter me-2"
                    onClick={toggleFilterVisibility}
                  >
                    <Filter className="filter-icon" />
                  </button>
                </div>
              </div>
            </div>

            <div
              className={`card${isFilterVisible ? " visible" : ""}`}
              id="filter_inputs"
              style={{ display: isFilterVisible ? "block" : "none" }}
            >
              <div className="card-body pb-0">
                <div className="row">
                  <div className="col-lg-3">
                    <div className="input-blocks">
                      <Box className="info-img" />
                      <Select
                        className="select"
                        options={categories}
                        value={selectedCategory}
                        onChange={(selected) => {
                          setSelectedCategory(selected);
                          handleFilterChange();
                        }}
                        placeholder="Select Category"
                      />
                    </div>
                  </div>
                  <div className="col-lg-3">
                    <div className="input-blocks">
                      <Zap className="info-img" />
                      <Select
                        className="select"
                        options={brands}
                        value={selectedBrand}
                        onChange={(selected) => {
                          setSelectedBrand(selected);
                          handleFilterChange();
                        }}
                        placeholder="Select Brand"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table datanew">
                <thead>
                  <tr>
                    <th className="no-sort">
                      <label className="checkboxs">
                        <input type="checkbox" id="select-all" />
                        <span className="checkmarks" />
                      </label>
                    </th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Sub Category</th>
                    <th>Brand</th>
                    <th>Unit</th>
                    <th>Instock Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : inventoryData.length > 0 ? (
                    inventoryData.map((item) => (
                      <tr key={item._id}>
                        <td>
                          <label className="checkboxs">
                            <input type="checkbox" />
                            <span className="checkmarks" />
                          </label>
                        </td>
                        <td className="productimgname">{item.productName}</td>
                        <td>{item.category || "N/A"}</td>
                        <td>{item.subcategories[0]?.name || "N/A"}</td>
                        <td>{item.brand || "N/A"}</td>
                        <td>{item.unit || "N/A"}</td>
                        <td>{item.stockQty}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No inventory data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryReport;
